import { ref, computed } from "vue";
import {
  getConsumoMPPlanReal,
  getConsumoMPTPU,
} from "@/utils/api_materia_prima.js";

// ── Fechas por defecto ───────────────────────────────────────────────────────

/** 'YYYY-MM-DD' local, sin pasar por toISOString() (evita el corrimiento
 *  de zona horaria de convertir a UTC). */
function aISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Mismo criterio que el default anterior por semana: Periodo A = del 1 de
 * enero a la mitad de los días transcurridos del año; Periodo B = de esa
 * mitad+1 días hasta hoy.
 */
function periodosDefecto() {
  const hoy = new Date();
  const inicioAnio = new Date(hoy.getFullYear(), 0, 1);
  const diasTranscurridos = Math.floor((hoy - inicioAnio) / 86400000);
  const mitad = Math.max(1, Math.floor(diasTranscurridos / 2));

  const fechaMitad = new Date(inicioAnio);
  fechaMitad.setDate(fechaMitad.getDate() + mitad);

  const fechaMitadSiguiente = new Date(fechaMitad);
  fechaMitadSiguiente.setDate(fechaMitadSiguiente.getDate() + 1);

  // Si el año apenas empieza, la "mitad+1" puede caer después de hoy —
  // en ese caso Periodo B colapsa a un solo día (hoy) en vez de quedar invertido.
  const inicioB = fechaMitadSiguiente > hoy ? hoy : fechaMitadSiguiente;

  return {
    A: { fecha_inicio: aISO(inicioAnio), fecha_fin: aISO(fechaMitad) },
    B: { fecha_inicio: aISO(inicioB), fecha_fin: aISO(hoy) },
  };
}

// ── Composable ─────────────────────────────────────────────────────────────

export function useConsumoMateriaPrima() {
  const cargando = ref(false);
  const error    = ref(null);

  const defaults = periodosDefecto();
  const periodoA = ref({ ...defaults.A });
  const periodoB = ref({ ...defaults.B });

  // Datos de la gráfica (Periodo A únicamente)
  const semanasPlanReal = ref([]);   // [{ semana, label, plan, real, porcentaje }]
  const totalPiezasA    = ref(0);
  const totalPiezasB    = ref(0);

  // Datos de la tabla TPU
  const topTPU_A = ref([]);          // [{ tpu, cantidad_kg }]
  const topTPU_B = ref([]);
  const totalKgA = ref(0);
  const totalKgB = ref(0);

  // Combinar ambas listas TPU en filas para la tabla
  const filasTPU = computed(() => {
    const mapaB = Object.fromEntries(topTPU_B.value.map((t) => [t.tpu, t.cantidad_kg]));
    const todos  = new Set([
      ...topTPU_A.value.map((t) => t.tpu),
      ...topTPU_B.value.map((t) => t.tpu),
    ]);
    return [...todos].map((nombre) => {
      const a = topTPU_A.value.find((t) => t.tpu === nombre);
      return {
        tpu:  nombre,
        kg_a: a?.cantidad_kg ?? 0,
        kg_b: mapaB[nombre]  ?? 0,
      };
    }).sort((a, b) => b.kg_a - a.kg_a);
  });

  // ── Etiqueta del rango ────────────────────────────────────────────────────

  const MESES = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic",
  ];

  function formatearDia(fechaIso) {
    const [, mes, dia] = fechaIso.split("-").map(Number);
    return `${dia} ${MESES[mes - 1]}`;
  }

  function etiquetaPeriodo(periodo) {
    if (!periodo?.fecha_inicio || !periodo?.fecha_fin) return "";
    const anio = periodo.fecha_fin.split("-")[0];
    return `${formatearDia(periodo.fecha_inicio)} – ${formatearDia(periodo.fecha_fin)} ${anio}`;
  }

  // ── Carga de datos ─────────────────────────────────────────────────────────

  async function cargarPlanReal() {
    const [respA, respB] = await Promise.all([
      getConsumoMPPlanReal({
        fecha_inicio: periodoA.value.fecha_inicio,
        fecha_fin:    periodoA.value.fecha_fin,
      }),
      getConsumoMPPlanReal({
        fecha_inicio: periodoB.value.fecha_inicio,
        fecha_fin:    periodoB.value.fecha_fin,
      }),
    ]);

    semanasPlanReal.value = respA.semanas ?? [];
    totalPiezasA.value    = respA.total_real ?? 0;
    totalPiezasB.value    = respB.total_real ?? 0;
  }

  async function cargarTPU() {
    const [respA, respB] = await Promise.all([
      getConsumoMPTPU({
        fecha_inicio: periodoA.value.fecha_inicio,
        fecha_fin:    periodoA.value.fecha_fin,
      }),
      getConsumoMPTPU({
        fecha_inicio: periodoB.value.fecha_inicio,
        fecha_fin:    periodoB.value.fecha_fin,
      }),
    ]);

    topTPU_A.value = respA.top_tpu ?? [];
    totalKgA.value = respA.total_kg ?? 0;
    topTPU_B.value = respB.top_tpu ?? [];
    totalKgB.value = respB.total_kg ?? 0;
  }

  async function cargarTodo() {
    cargando.value = true;
    error.value    = null;
    try {
      await Promise.all([cargarPlanReal(), cargarTPU()]);
    } catch (e) {
      error.value = "No se pudieron cargar los datos.";
      console.error("Error al cargar ConsumoMP:", e);
    } finally {
      cargando.value = false;
    }
  }

  // ── Acciones del usuario ──────────────────────────────────────────────────

  function actualizarPeriodoA(nuevo) {
    periodoA.value = nuevo;
    cargarTodo();
  }

  function actualizarPeriodoB(nuevo) {
    periodoB.value = nuevo;
    cargarTodo();
  }

  return {
    cargando,
    error,
    periodoA,
    periodoB,
    semanasPlanReal,
    totalPiezasA,
    totalPiezasB,
    filasTPU,
    totalKgA,
    totalKgB,
    etiquetaPeriodo,
    cargarTodo,
    actualizarPeriodoA,
    actualizarPeriodoB,
  };
}